import { useEffect, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { useInView } from '#app/utils/use-in-view.ts'

export const marketingWrap = 'mx-auto w-full max-w-[1160px] px-6'

export const primaryCta =
	'inline-flex items-center justify-center gap-2 rounded-xl border border-transparent bg-gradient-to-br from-cyan-300 to-teal-400 px-5 py-3.5 text-center font-semibold text-[#021018] shadow-[0_10px_30px_-10px_rgba(45,212,191,0.6)] transition hover:brightness-105 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-cyan-300 motion-safe:hover:-translate-y-px'

export const secondaryCta =
	'inline-flex items-center justify-center gap-2 rounded-xl border border-slate-400/20 bg-white/[0.03] px-5 py-3.5 text-center font-semibold text-slate-100 transition hover:border-cyan-300 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-cyan-300'

/**
 * Fades content up the first time it scrolls into view. Skipped for reduced
 * motion. Stays visible until measured, and only hides content that starts
 * below the viewport. The transition turns on after that first pass so
 * sections already on screen do not fade.
 */
export function Reveal({
	children,
	className,
	delay = 0,
	style,
}: {
	children: ReactNode
	className?: string
	delay?: number
	style?: CSSProperties
}) {
	const { ref, isInView, hasMeasured, isBelowFold } = useInView({
		threshold: 0.15,
	})
	const [canAnimate, setCanAnimate] = useState(false)
	useEffect(() => {
		if (hasMeasured) setCanAnimate(true)
	}, [hasMeasured])
	const hidden = hasMeasured && isBelowFold && !isInView
	return (
		<div
			ref={ref as RefObject<HTMLDivElement>}
			className={cn(
				canAnimate &&
					'motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out',
				hidden && 'motion-safe:translate-y-6 motion-safe:opacity-0',
				className,
			)}
			style={{ ...style, transitionDelay: `${delay}ms` }}
		>
			{children}
		</div>
	)
}

export function SectionHeading({
	eyebrow,
	title,
	children,
	className,
	titleId,
	align = 'center',
	as: Heading = 'h2',
}: {
	eyebrow: string
	title: string
	children?: ReactNode
	className?: string
	titleId?: string
	align?: 'center' | 'left'
	as?: 'h1' | 'h2'
}) {
	const content = (
		<>
			<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">
				{eyebrow}
			</p>
			<Heading
				id={titleId}
				className="text-[clamp(1.8rem,3.6vw,2.6rem)] font-bold leading-[1.15] tracking-tight text-slate-50"
			>
				{title}
			</Heading>
			{children ? (
				<p
					className={cn(
						'mt-4 text-lg text-slate-400',
						align === 'center' && 'mx-auto',
					)}
				>
					{children}
				</p>
			) : null}
		</>
	)
	const frame = cn(
		'max-w-2xl',
		align === 'center' && 'mx-auto text-center',
		className,
	)
	// The page h1 stays outside the scroll animation so it is in the first paint.
	if (Heading === 'h1') {
		return <div className={frame}>{content}</div>
	}
	return <Reveal className={frame}>{content}</Reveal>
}
